// frontend/src/components/auth/ImpersonationBanner.tsx
/**
 * Admin "View as" (SA-906): the persistent bar above the app header while
 * the application answers for another user.
 *
 * It is also the ONE place an end is settled: whoever ends the view-as (Stop
 * here, the countdown here, a 401 in apiClient) only calls
 * `stopImpersonation(reason)` on the store; this component watches
 * `impersonationEnded`, runs the resets through `settleViewAsEnd`, words the
 * reason and returns to the Users page. Mounted in AppShell, so it is always
 * there to settle.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye } from 'lucide-react'
import { Button, useToast } from '@/components/ui'
import { useAuthStore } from '@/stores/authStore'
import { settleViewAsEnd } from '@/services/impersonation'
import { endedText, KEYS_UNAVAILABLE, parseExpiry, remainingText } from './impersonationText'

const TICK_MS = 60_000

export function ImpersonationBanner() {
  const impersonation = useAuthStore((s) => s.impersonation)
  const user = useAuthStore((s) => s.user)
  const ended = useAuthStore((s) => s.impersonationEnded)
  const navigate = useNavigate()
  const { addToast } = useToast()
  // Held in refs so the settle effect runs once per end: useNavigate's identity
  // changes with the location, and a re-run mid-settle would settle twice.
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate
  const addToastRef = useRef(addToast)
  addToastRef.current = addToast
  const [now, setNow] = useState(() => Date.now())

  // The countdown re-renders on its own clock: nothing else re-renders this bar.
  useEffect(() => {
    if (!impersonation) return
    setNow(Date.now())
    const id = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(id)
  }, [impersonation])

  // D10: the browser ends the view-as itself at expiry — at once when the
  // stamp has already passed (a tab back from sleep, clock skew).
  useEffect(() => {
    if (!impersonation) return
    const left = parseExpiry(impersonation.expiresAt) - Date.now()
    if (!(left > 0)) {
      useAuthStore.getState().stopImpersonation('expired')
      return
    }
    const id = setTimeout(() => useAuthStore.getState().stopImpersonation('expired'), left)
    return () => clearTimeout(id)
  }, [impersonation])

  // Settle an end, whoever recorded it.
  useEffect(() => {
    if (!ended) return
    let cancelled = false
    void settleViewAsEnd().then((settled) => {
      if (cancelled || !settled) return
      const text = endedText(settled.reason)
      if (text) addToastRef.current({ type: 'warning', title: text })
      navigateRef.current('/admin/users')
    })
    return () => {
      cancelled = true
    }
  }, [ended])

  if (!impersonation || !user) return null

  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-warning/30 bg-warning/10 px-4 py-2 text-sm text-warning-strong"
    >
      <Eye className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="font-medium">
        Viewing as {user.display_name} ({user.email})
      </span>
      <span aria-hidden="true">·</span>
      <span>{remainingText(impersonation.expiresAt, now)}</span>
      <span aria-hidden="true">·</span>
      {/* SA-1034 PR 5 (spec §3.4): the per-user key reads are refused under a view-as by design; say so here, once. */}
      <span>{KEYS_UNAVAILABLE}</span>
      <span aria-hidden="true">·</span>
      <Button size="sm" variant="outline" onClick={() => useAuthStore.getState().stopImpersonation('stopped')}>
        Stop
      </Button>
      <span className="ml-auto text-xs opacity-80">A reload ends the view-as.</span>
    </div>
  )
}
